import * as React from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment-timezone';
import DeleteIconButton from '../../components/DeleteIconButton';
import FileActionRow from '../../components/FileActionRow';
import { toTitleCase } from '../../utils';
import { NO_TICKET_CHANGES } from './ticketChanges';
import { TICKET_FORMATS, TICKET_FORMAT_FILE, TICKET_LINK_FORMATS, TICKET_UNITS, TICKET_UNIT_GROUP } from './utils';

// CP-5799: the participant panels' Tickets, built like Attachments (Figma "Tickets section"): one row per ticket
// -- the name opens it, Replace (file tickets), trash -- and [+ Add ticket], which picks a ticket file (PDF, a
// picture or an Apple Wallet pass). Nothing is saved here: picked files and removals are staged in `changes`
// (ticketChanges.js) and go to the backend with the panel's SAVE / Add user, so CANCEL drops them like it drops
// an attachment. `tickets` are the booking's saved ones (`tickets` of GET tour_user/<id>/; none for a new user).

// What the backend takes as a ticket file; it checks the bytes, this only steers the file picker.
const FILE_ACCEPT = '.pdf,.pkpass,application/pdf,application/vnd.apple.pkpass,image/jpeg,image/png,image/gif,image/webp';

// Redeemed, no-showed or scanned: the backend refuses to change such a ticket (removing it stays possible).
export const isUsedTicket = (ticket) => !!ticket.redeemed_at || !!ticket.noshowed_at || (ticket.use_count || 0) > 0;

const usedText = (ticket) => {
    if (ticket.redeemed_at) {
        return `Redeemed at ${moment(ticket.redeemed_at).format('YYYY-MM-DD HH:mm Z')}`;
    }
    if (ticket.noshowed_at) {
        return `No-show at ${moment(ticket.noshowed_at).format('YYYY-MM-DD HH:mm Z')}`;
    }
    return ticket.use_count > 0 ? `Used ${ticket.use_count} time${ticket.use_count === 1 ? '' : 's'}` : null;
};

// The grey row Attachments use.
const ROW_STYLE = { backgroundColor: '#F5F5F5', minHeight: '48px', borderRadius: '8px', display: 'flex', marginLeft: '0.75rem', minWidth: 0 };

// The row's one child: the name line, and under it the details line when there is one. Laid out here rather
// than on the row, whose flex direction did not hold on staging (Rea, CP-5799 10/22: the details were pressed
// in beside the trash as "F…").
const ROW_BODY_SX = { display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%', minWidth: 0 };

const Row = ({ children }) => (
    <Grid item xs={12} style={ROW_STYLE} mt={0.5} px={2} py={1} container alignItems="center">
        <Box sx={ROW_BODY_SX}>{children}</Box>
    </Grid>
);

const BookingTickets = ({ tickets, changes, onChangesChange, integration, canEdit, tooltipProps }) => {
    const staged = changes || NO_TICKET_CHANGES;
    const inputRef = React.useRef(null);
    const pickFor = React.useRef(null); // what the picker is open for: { add } | { replaceId } | { replaceKey }
    const nextKey = React.useRef(1);
    const saved = (tickets || []).filter((t) => !staged.removed.includes(t.id));

    if (!canEdit && saved.length === 0) {
        return null;
    }

    const update = (patch) => onChangesChange({ ...staged, ...patch });

    const openPicker = (target) => {
        pickFor.current = target;
        inputRef.current && inputRef.current.click();
    };

    const onFilePicked = (ev) => {
        const file = ev.target.files && ev.target.files[0];
        ev.target.value = ''; // picking the same file again still fires onChange
        const target = pickFor.current;
        if (!file || !target) {
            return;
        }
        if (target.replaceId) {
            update({ replaced: { ...staged.replaced, [target.replaceId]: file } });
        } else if (target.replaceKey) {
            update({ added: staged.added.map((a) => (a.key === target.replaceKey ? { ...a, file } : a)) });
        } else {
            update({ added: [...staged.added, { key: `new-${nextKey.current++}-${Date.now()}`, file }] });
        }
    };

    const removeSaved = (ticket) => {
        const { [ticket.id]: _dropped, ...replaced } = staged.replaced;
        update({ removed: [...staged.removed, ticket.id], replaced });
    };

    const savedRow = (ticket) => {
        const replacement = staged.replaced[ticket.id];
        const isFile = ticket.format === TICKET_FORMAT_FILE;
        const link = replacement || (isFile ? ticket.file : TICKET_LINK_FORMATS.includes(ticket.format) ? ticket.value : null);
        const name = ticket.label || TICKET_FORMATS[ticket.format] || 'Ticket';
        // A file ticket reads like an attachment: no details line unless there is something to say.
        const details = [
            // The kind, unless the name already is the kind (no label).
            replacement ? `New file: ${replacement.name}` : isFile || !ticket.label ? null : TICKET_FORMATS[ticket.format],
            !isFile && !TICKET_LINK_FORMATS.includes(ticket.format) ? ticket.value : null,
            ticket.unit === TICKET_UNIT_GROUP ? TICKET_UNITS[TICKET_UNIT_GROUP] : null,
            usedText(ticket),
        ].filter(Boolean);
        const remove = canEdit ? () => removeSaved(ticket) : undefined;
        return (
            <Row key={ticket.id}>
                {link ? (
                    <FileActionRow
                        name={name}
                        file={link}
                        onReplace={canEdit && isFile && !isUsedTicket(ticket) ? () => openPicker({ replaceId: ticket.id }) : undefined}
                        onDelete={remove}
                        replaceTitle="Replace ticket"
                        deleteTitle="Remove ticket"
                        actionsAtEnd
                        tooltipProps={tooltipProps}
                        sx={{ width: '100%' }}
                    />
                ) : (
                    // A QR code or a reference has nothing to open: its value is on the line below.
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                        <Typography noWrap sx={{ minWidth: 0 }}>
                            {name}
                        </Typography>
                        {remove && (
                            <Box sx={{ ml: 'auto', flex: 'none' }}>
                                <DeleteIconButton title="Remove ticket" tooltipProps={tooltipProps} onClick={remove} />
                            </Box>
                        )}
                    </Box>
                )}
                {details.length > 0 && (
                    <Typography variant="caption" color="textSecondary" component="div" noWrap data-testid="ticket-details">
                        {details.join(' · ')}
                    </Typography>
                )}
            </Row>
        );
    };

    const addedRow = (added) => (
        <Row key={added.key}>
            <FileActionRow
                name={added.file.name}
                file={added.file}
                onReplace={() => openPicker({ replaceKey: added.key })}
                onDelete={() => update({ added: staged.added.filter((a) => a.key !== added.key) })}
                replaceTitle="Replace ticket"
                deleteTitle="Remove ticket"
                actionsAtEnd
                tooltipProps={tooltipProps}
                sx={{ width: '100%' }}
            />
        </Row>
    );

    return (
        <>
            <Grid item xs={12} mt={2}>
                <Typography fontSize={16} fontWeight={500}>
                    Tickets
                </Typography>
                {canEdit && integration && (
                    <Typography variant="caption" color="textSecondary" component="div">
                        This booking is synced from {toTitleCase(integration)}. A sync that sends tickets replaces the ones added here.
                    </Typography>
                )}
            </Grid>
            {saved.map(savedRow)}
            {staged.added.map(addedRow)}
            {canEdit && (
                <Grid item xs={12} color="primary">
                    <input ref={inputRef} type="file" accept={FILE_ACCEPT} hidden onChange={onFilePicked} data-testid="ticket-file-input" />
                    <Button variant="outlined" onClick={() => openPicker({ add: true })}>
                        <AddIcon fontSize="small" />
                        &nbsp;Add ticket
                    </Button>
                </Grid>
            )}
        </>
    );
};

export default BookingTickets;
